import { useState, useCallback } from 'react';

interface EventKey {
  eventName: string;
  userId?: string;
  additionalKey?: string;
}

interface EventRecord {
  timestamp: number;
  count: number;
}

/**
 * Hook to prevent duplicate events from being sent to analytics services
 * Used to solve Customer.io duplicate email issue
 */
export function useEventDeduplication(windowMs: number = 5000) {
  const [eventHistory, setEventHistory] = useState<Map<string, EventRecord>>(new Map());

  const shouldPreventDuplicateEvent = useCallback((eventKey: EventKey): boolean => {
    const key = `${eventKey.eventName}_${eventKey.userId || 'anonymous'}_${eventKey.additionalKey || ''}`;
    const now = Date.now();
    const existing = eventHistory.get(key);

    if (existing && (now - existing.timestamp) < windowMs) {
      console.log(`🚫 Preventing duplicate event: ${key} (last fired ${now - existing.timestamp}ms ago)`);
      
      // Update count but keep blocking
      setEventHistory(prev => new Map(prev.set(key, {
        timestamp: existing.timestamp,
        count: existing.count + 1
      })));
      
      return true;
    }

    // Record the event
    setEventHistory(prev => new Map(prev.set(key, {
      timestamp: now,
      count: 1
    })));
    
    return false;
  }, [eventHistory, windowMs]);

  const clearEventHistory = useCallback(() => {
    setEventHistory(new Map());
  }, []);

  return {
    shouldPreventDuplicateEvent,
    clearEventHistory,
    getEventCount: (eventKey: EventKey) => {
      const key = `${eventKey.eventName}_${eventKey.userId || 'anonymous'}_${eventKey.additionalKey || ''}`;
      return eventHistory.get(key)?.count || 0;
    }
  };
}
